import { persistentAtom, type PersistentEncoder } from '@nanostores/persistent';
import type { WritableAtom } from 'nanostores';

type PersistentAtomOptions<T> = PersistentEncoder<T> & { listen?: boolean };

/**
 * `persistentAtom` whose value never reads as `undefined`.
 *
 * `@nanostores/persistent` 0.10 reacts to another tab clearing storage (or
 * removing this atom's key) by resetting the mounted atom to `undefined`
 * instead of `initial`; 1.x fixed that but needs a nanostores major bump.
 * Until then, every consumer reading a property off the value crashed into the
 * nearest error boundary the moment a sibling tab ran `localStorage.clear()`
 * (CON-7753). Here `get()` falls back to `initial`, and a listener that would
 * have seen `undefined` resets the atom to `initial` instead, which re-persists
 * it and notifies every subscriber with a real value. Only `undefined` is
 * treated this way: a persisted `null` is a legitimate value on both paths.
 * An `initial` of `undefined` disables the fallback, since the consumer then
 * accepts `undefined` as a value (and resetting to it would notify forever).
 */
export function persistentAtomWithFallback<T>(
  name: string,
  initial: T,
  opts: PersistentAtomOptions<T>,
): WritableAtom<T> {
  const store = persistentAtom<T>(name, initial, opts);

  // nanostores' runtime contract is wider than its 0.7 typings: listeners
  // receive `(value, changedKey)` and `listen`/`subscribe` take `(listener,
  // level)`. `computed` orders updates by the level and `use-partial-store`
  // reads the changed key, so the wrappers forward every argument.
  type Listener = Parameters<typeof store.listen>[0];
  type Listen = (listener: Listener, ...rest: unknown[]) => () => void;
  const get: () => T | undefined = store.get;
  const listen: Listen = store.listen;
  const subscribe: Listen = store.subscribe;

  const healed = (listener: Listener): Listener => {
    const forward: (value: Parameters<Listener>[0], ...rest: unknown[]) => void = listener;
    return (value, ...rest: unknown[]) => {
      const current: typeof value | undefined = value;
      if (current === undefined && initial !== undefined) {
        // Every listener is notified with the same `undefined`; the first one
        // resets the atom, which queues `initial` for all of them.
        if (get() === undefined) store.set(initial);
        return;
      }
      forward(value, ...rest);
    };
  };

  store.get = () => {
    const value = get();
    if (value === undefined) return initial;
    return value;
  };
  store.listen = (listener, ...rest: unknown[]) => listen(healed(listener), ...rest);
  store.subscribe = (listener, ...rest: unknown[]) => subscribe(healed(listener), ...rest);

  return store;
}
